import triggerTracking from '@blueprint/tracking/dist/Tracking';
import { Box } from '@zoopla-eng/zds-components/box';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { EmailMedium, SaveMedium } from '@zoopla-eng/zds-components/icons';
import { Link } from '@zoopla-eng/zds-components/link';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { AnalyticsTaxonomy, Links } from '@zoopla/listing-search-context';
import { useGetMapViewUrl } from '@zoopla/listing-search/use-map-view-url';
import { useOptimizelyFeatureFlag } from '@zoopla/optimizely';
import { getUserSelector, useIsLoggedIn } from '@zoopla/redux-reducer-user';
import {
    ANALYTICS_BASE_TAXONOMY,
    Taxonomy,
    updateUserTaxonomyKeys,
    UserTaxonomy,
    useTrackSegmentEvent
} from '@zoopla/tracking';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import AlertContent from './AlertContent';
import AlertModalWrapper from './AlertModalWrapper';
import {
    trackCreateEmailAlert,
    trackSaveThisSearch
} from './SaveThisSearchModal.tracking';
import { SaveThisSearchModalWithAuth } from './SaveThisSearchModalWithAuth';
import { useSaveThisSearchModal } from './useSaveThisSearchModal';

type AlertPanelProps = {
    links: Links;
    analyticsTaxonomy?: AnalyticsTaxonomy;
};

export const AlertPanel: FC<React.PropsWithChildren<AlertPanelProps>> = ({
    links,
    analyticsTaxonomy
}) => {
    const [isSaveEmailAlertModalEnabled] = useOptimizelyFeatureFlag(
        'sr_1249_save_email_alert_modal'
    );

    const isLoggedIn = useIsLoggedIn();

    const {
        showAuthModal,
        setShowAuthModal,
        defaultAlertFrequency,
        setDefaultAlertFrequency,
        showSaveThisSearchModal,
        setShowSaveThisSearchModal
    } = useSaveThisSearchModal();

    const timeTravelSearchHref = '/travel-time/';
    const drawYourSearchHref = useGetMapViewUrl(true);

    const userTaxonomy = useSelector(getUserSelector);
    const trackSegmentEvent = useTrackSegmentEvent();

    return isSaveEmailAlertModalEnabled ? (
        <Box
            px="$S500"
            py="$M100"
            width="100%"
            {...{ boxShadow: '$100', borderRadius: '$100' }}
        >
            <Stack rowGap="$S400">
                <SaveThisSearchModalWithAuth
                    {...{
                        showAuthModal,
                        setShowAuthModal,
                        defaultAlertFrequency,
                        setDefaultAlertFrequency,
                        showSaveThisSearchModal,
                        setShowSaveThisSearchModal
                    }}
                />
                <ButtonLeft
                    category="secondary"
                    icon={<EmailMedium />}
                    onClick={() => {
                        setDefaultAlertFrequency('0');

                        trackCreateEmailAlert();

                        trackSegmentEvent(
                            'Click Email Alert',
                            {
                                ...ANALYTICS_BASE_TAXONOMY,
                                ...(analyticsTaxonomy as unknown as Taxonomy),
                                ...(updateUserTaxonomyKeys(
                                    userTaxonomy
                                ) as unknown as UserTaxonomy),
                                signedInStatus: isLoggedIn
                                    ? 'signed in'
                                    : 'signed out',
                                location: analyticsTaxonomy?.areaName
                            },
                            undefined
                        );

                        if (!isLoggedIn) {
                            setShowAuthModal(true);
                        } else {
                            setShowSaveThisSearchModal(true);
                        }
                    }}
                >
                    Create email alert
                </ButtonLeft>

                <ButtonLeft
                    category="secondary"
                    icon={<SaveMedium />}
                    onClick={() => {
                        setDefaultAlertFrequency('');

                        trackSaveThisSearch();

                        trackSegmentEvent(
                            'Click Save Search',
                            {
                                ...ANALYTICS_BASE_TAXONOMY,
                                ...(analyticsTaxonomy as unknown as Taxonomy),
                                ...(updateUserTaxonomyKeys(
                                    userTaxonomy
                                ) as unknown as UserTaxonomy),
                                signedInStatus: isLoggedIn
                                    ? 'signed in'
                                    : 'signed out',
                                location: analyticsTaxonomy?.areaName
                            },
                            undefined
                        );

                        if (!isLoggedIn) {
                            setShowAuthModal(true);
                        } else {
                            setShowSaveThisSearchModal(true);
                        }
                    }}
                >
                    Save this search
                </ButtonLeft>

                <Divider direction="horizontal" />
                <Link href={timeTravelSearchHref}>Travel time search</Link>
                <Link
                    onClick={() => {
                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: 'search results',
                            eventAction: 'clickthrough',
                            eventLabel: 'draw_search'
                        });
                    }}
                    href={drawYourSearchHref}
                >
                    Draw your search
                </Link>
            </Stack>
        </Box>
    ) : (
        <AlertModalWrapper>
            <AlertContent links={links} taxonomy={analyticsTaxonomy} />
        </AlertModalWrapper>
    );
};
